import { IEditableColumn, Modal } from '@aon/aon-tws-common-ui';
import { EditableColumnType } from '@aon/aon-tws-common-ui/build/lib/components/EditableTable';
import HtmlParser from 'html-react-parser';
import _ from 'lodash';
import * as React from 'react';
import styled from 'styled-components';
import ContentConstants from '../../contentConstants';
import { EditState, ITranslationEditItem } from '../../models';
import { TranslationHelper } from '../../utilities/TranslationHelper';
import { WarningModal } from '../common/WarningModal';
import { TranslationEditor } from './TranslationEditor';

export interface ITranslationEditorModalProps {
	enableHtmlEditor: boolean;
	isOpen: boolean;
	headerTitle?: string;
	inputType: EditableColumnType;
	maxLength?: number;
	property: string;
	propertyDisplayName: string;
	defaultTranslation?: ITranslationEditItem;
	translations: ITranslationEditItem[];
	lastSavedTranslations: ITranslationEditItem[];
	primaryButtonText: string;
	saveTranslations: (items: ITranslationEditItem[]) => void;
	onClose: () => void;
}
export interface ITranslationEditorModalState {
	translations: ITranslationEditItem[];
	translationsModified: boolean;
	openWarningModal: boolean;
}
export const columns: IEditableColumn[] = [
	{ columnKey: 'language', label: 'Language', editable: false },
	{ columnKey: 'textValue', label: 'Translation', editable: true, placeholder: 'Translation text', type: 'textarea' },
	{ columnKey: 'modifiedOn', label: 'Updated', editable: false, dataType: 'date', format: 'MMM DD, YYYY hh:mm A' },
];

// base language
export const BaseLanguageContainer = styled.div`
	margin-bottom: 50px;
	width: 100%;

	@media (max-width: 795px) {
		margin-bottom: 15px;
		width: 100%;
	}
`;
export const BaseLanguageTitleContainer = styled('div')<{ size?: 'sm' | undefined }>`
	display: inline-block;
	height: 100%;
	vertical-align: middle;
	width: 16%;
	padding-right: 10px;

	@media (max-width: 795px) {
		display: block;
		padding: 5px 0;
		width: 100%;
	}
`;
export const BaseLanguageTextDisplayContainer = styled('div')<{ size?: 'sm' | undefined }>`
	display: inline-block;
	font-size: 13px;
	height: 100%;
	vertical-align: middle;
	width: 84%;

	@media (max-width: 795px) {
		display: block;
		padding: 5px 0;
		width: 100%;
	}
`;
export const BaseLanguageTextDisplay = styled.div`
	color: #bbbbbb;
	background-color: rgb(243, 243, 243);
	border-color: rgb(243, 243, 243);
	border-radius: 3px;
	min-height: 40px;
	padding: 10px;
	word-break: break-word;
`;

export const Content = styled.div`
	width: 100%;
`;

export class TranslationEditorModal extends React.Component<ITranslationEditorModalProps, ITranslationEditorModalState> {
	public static defaultProps: Partial<ITranslationEditorModalProps> = {
		headerTitle: ContentConstants.TITLE_TRANSLATION_EDITOR,
	};
	constructor(props: ITranslationEditorModalProps) {
		super(props);

		this.state = {
			translations: this.prepTranslations(this.props),
			translationsModified: false,
			openWarningModal: false,
		};

		this.handleOnSubmit = this.handleOnSubmit.bind(this);
		this.handleOnChange = this.handleOnChange.bind(this);
		this.prepTranslations = this.prepTranslations.bind(this);
		this.handleWarningModalClose = this.handleWarningModalClose.bind(this);
		this.handleWarningModalSubmit = this.handleWarningModalSubmit.bind(this);
		this.checkUnsavedChangesOnClose = this.checkUnsavedChangesOnClose.bind(this);
	}

	public UNSAFE_componentWillReceiveProps(props: ITranslationEditorModalProps) {
		this.setState({ translations: this.prepTranslations(props), translationsModified: false });
	}

	public prepTranslations(props: ITranslationEditorModalProps): ITranslationEditItem[] {
		const copiedTranslations = _.cloneDeep(props.translations);
		const mappedTranslations = _.map(copiedTranslations, (t: ITranslationEditItem) => {
			if (!t.textValue) {
				t.modifiedOn = undefined;
			}
			return t;
		});
		return _.orderBy(mappedTranslations, ['language', 'cultureCode']);
	}

	public render() {
		const { enableHtmlEditor, headerTitle, property, inputType, maxLength, propertyDisplayName, isOpen, primaryButtonText } = this.props;
		const { translations, translationsModified } = this.state;
		const hasDefaultTranslation = this.hasDefaultTranslation();

		return (
			<Modal
				isOpen={isOpen}
				hideFooter={false}
				hideHeader={false}
				contenthcentered={true}
				contentfullwidth={true}
				title={headerTitle}
				onSubmit={this.handleOnSubmit}
				primaryButtonText={primaryButtonText}
				primaryButtonDisabled={!hasDefaultTranslation || translations.length === 0 || !translationsModified}
				onClosed={this.checkUnsavedChangesOnClose}
				secondaryButtonText={ContentConstants.BUTTONS_CANCEL}
				secondaryButtonDisabled={false}
				onSecondaryButtonClick={this.props.onClose}
			>
				<Content>
					<BaseLanguageContainer>
						<BaseLanguageTitleContainer size={'sm'}>
							<strong>{propertyDisplayName}</strong>
						</BaseLanguageTitleContainer>
						<BaseLanguageTextDisplayContainer>
							<BaseLanguageTextDisplay>{HtmlParser(this.getDefaultTranslation())}</BaseLanguageTextDisplay>
						</BaseLanguageTextDisplayContainer>
					</BaseLanguageContainer>
					<TranslationEditor
						inputType={inputType}
						maxLength={maxLength}
						enableHtmlEditor={enableHtmlEditor}
						hasDefaultTranslation={hasDefaultTranslation}
						property={property}
						propertyDisplayName={propertyDisplayName}
						onChange={this.handleOnChange}
						translations={translations}
					/>
				</Content>
				<WarningModal
					isOpen={this.state.openWarningModal}
					onClose={this.handleWarningModalClose}
					onSubmit={this.handleWarningModalSubmit}
					contentHeadline={ContentConstants.WARNING_MODAL_UNSAVED_HEADLINE}
					contentBody={ContentConstants.WARNING_MODAL_UNSAVED_BODY}
				/>
			</Modal>
		);
	}

	public handleWarningModalClose() {
		this.setState({ openWarningModal: false });
	}

	public handleWarningModalSubmit() {
		this.setState({ openWarningModal: false });
		this.props.onClose();
	}

	public checkUnsavedChangesOnClose() {
		if (this.state.translationsModified) {
			this.setState({ openWarningModal: true });
		} else {
			this.props.onClose();
		}
	}

	public handleOnSubmit() {
		const { translations } = this.state;

		this.props.saveTranslations(translations);
	}

	public handleOnChange(translations: ITranslationEditItem[]) {
		const nextTranslations = TranslationHelper.updateTranslationEditState(translations, this.props.lastSavedTranslations);

		this.setState({
			translations: nextTranslations,
			translationsModified: _.some(nextTranslations, (i: ITranslationEditItem) => i.editState !== EditState.None),
		});
	}

	private hasDefaultTranslation() {
		const { defaultTranslation } = this.props;

		return (
			defaultTranslation !== undefined &&
			defaultTranslation !== null &&
			typeof defaultTranslation.textValue === typeof 'string' &&
			defaultTranslation.textValue.trim().length > 0
		);
	}

	private getDefaultTranslation() {
		const { defaultTranslation } = this.props;
		if (!this.hasDefaultTranslation()) {
			return '--';
		}
		// null check is to satisfy typescript
		return defaultTranslation!.textValue;
	}
}
